// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
// eslint-disable-next-line no-restricted-imports
import "@sendbird/uikit-react/dist/index.css";

import { Avatar, CbhIcon, Divider, Text, useModalState } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings } from "@clipboard-health/ui-theme";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  ButtonBase,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import { type OpenChannel } from "@sendbird/chat/openChannel";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { isEmpty } from "lodash";
import pluralize from "pluralize";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";

import { IconButton } from "../redesign/components/IconButton";
import { PageWrapper } from "../redesign/components/PageWrapper";
import { Navbar } from "../redesign/Navbar";
import { SHIFT_DISCOVERY_PATH } from "../redesign/ShiftDiscovery/paths";
import { CreateChannelBottomSheet } from "./CreateChannelBottomSheet";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  return debouncedValue;
}

interface SearchBarProps {
  onSearch: (query: string) => void;
}

function SearchBar({ onSearch }: SearchBarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    onSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, onSearch]);

  return (
    <TextField
      fullWidth
      placeholder="Search chatrooms..."
      value={searchQuery}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
      onChange={(event) => {
        setSearchQuery(event.target.value);
      }}
    />
  );
}

function ChannelListStack({
  channels,
  favorites,
  onToggleFavorite,
}: {
  channels: OpenChannel[];
  favorites: string[];
  onToggleFavorite: (channelUrl: string) => void;
}) {
  const history = useHistory();
  const match = useRouteMatch();

  return (
    <>
      {channels.map((channel) => (
        <Card key={channel.url}>
          <CardActionArea
            sx={{ flexGrow: 1 }}
            onClick={() => {
              const chatroomId = channel.url;
              history.push(`${match.url}/${chatroomId}`);
            }}
          >
            <CardContent>
              <Stack direction="column" spacing={3}>
                <Stack
                  spacing={1}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Stack direction="row" spacing={4} alignItems="center">
                      <Avatar size="small" src={channel.coverUrl} alt={channel.name} />
                      <Text semibold variant="body1">
                        {channel.name}
                      </Text>
                    </Stack>
                  </Stack>
                  <ButtonBase
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleFavorite(channel.url);
                    }}
                  >
                    <CbhIcon
                      type={favorites.includes(channel.url) ? "star-filled" : "star"}
                      color={(theme) =>
                        favorites.includes(channel.url) ? "#FFCC00" : theme.palette.primary.main
                      }
                    />
                  </ButtonBase>
                </Stack>
                {channel.participantCount > 0 && (
                  <Text semibold variant="caption">
                    ({channel.participantCount} {pluralize("member", channel.participantCount)})
                  </Text>
                )}
                {!isEmpty(channel.data) && (
                  <>
                    <Divider color="primary" />
                    <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                      {channel.data}
                    </Text>
                  </>
                )}
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      ))}
    </>
  );
}

function ChannelList({
  channels,
  loading,
  favorites,
  onToggleFavorite,
}: {
  channels: OpenChannel[];
  loading: boolean;
  favorites: string[];
  onToggleFavorite: (channelUrl: string) => void;
}) {
  if (loading && channels.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (channels.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Text variant="body1">No chatrooms available</Text>
      </Box>
    );
  }

  return (
    <Stack spacing={5}>
      {favorites.length > 0 && (
        <>
          <Text bold variant="h6">
            Favorites
          </Text>

          <ChannelListStack
            channels={channels.filter((channel) => favorites.includes(channel.url))}
            favorites={favorites}
            onToggleFavorite={onToggleFavorite}
          />
        </>
      )}

      <Text bold variant="h6">
        All chatrooms
      </Text>

      <ChannelListStack
        channels={channels.filter((channel) => !favorites.includes(channel.url))}
        favorites={favorites}
        onToggleFavorite={onToggleFavorite}
      />
    </Stack>
  );
}

export function CreateChannelCard({
  loadChannels,
}: {
  loadChannels: (searchQuery?: string) => Promise<void>;
}): JSX.Element {
  const modalState = useModalState();
  const sendbirdState = useSendbirdStateContext();
  const sdk = sendbirdState?.stores?.sdkStore?.sdk;

  const handleSubmit = async (data: { name: string; description: string }) => {
    if (!sdk?.openChannel) {
      return;
    }

    try {
      const channel = await sdk.openChannel.createChannel({
        name: data.name,
        // coverUrl: "", // Optional: You can add a cover image URL here
        customType: "", // Optional: You can add a custom type for filtering
        data: data.description, // Store description in channel data
        operatorUserIds: [], // Optional: Add operator user IDs if needed
      });

      // Enter the channel after creation
      await channel.enter();
      modalState.closeModal();
      // Refetch channels after successful creation
      await loadChannels();
    } catch {
      // You might want to show an error message to the user here
    }
  };

  return (
    <>
      <Card>
        <CardActionArea onClick={modalState.openModal}>
          <CardContent>
            <Text bold variant="body1">
              Create new chatroom
            </Text>
          </CardContent>
        </CardActionArea>
      </Card>
      <CreateChannelBottomSheet modalState={modalState} onSubmit={handleSubmit} />
    </>
  );
}

export function ChatroomsPage(): JSX.Element {
  const history = useHistory();
  const [channels, setChannels] = useState<OpenChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useLocalStorage<string[]>("chatroom-favorites", []);
  const sendbirdState = useSendbirdStateContext();
  const sdk = sendbirdState?.stores?.sdkStore?.sdk;

  const handleToggleFavorite = useCallback(
    (channelUrl: string) => {
      setFavorites((previous: string[]) => {
        if (previous.includes(channelUrl)) {
          return previous.filter((url: string) => url !== channelUrl);
        }

        return [...previous, channelUrl];
      });
    },
    [setFavorites]
  );

  const loadChannels = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    async (searchQuery: string = "") => {
      if (!sdk?.openChannel) {
        return;
      }

      try {
        setLoading(true);
        const query = sdk.openChannel.createOpenChannelListQuery({
          limit: 20,
          nameKeyword: isEmpty(searchQuery) ? undefined : searchQuery,
        });

        const newChannels = await query.next();
        setChannels((previous) => {
          // If this is a new search or initial load, replace all channels
          if (isEmpty(searchQuery) && previous.length === 0) {
            return newChannels;
          }

          // Otherwise, append new channels, filtering out any duplicates
          const existingUrls = new Set(previous.map((channel) => channel.url));
          const uniqueNewChannels = newChannels.filter((channel) => !existingUrls.has(channel.url));
          return [...previous, ...uniqueNewChannels];
        });
      } catch {
      } finally {
        setLoading(false);
      }
    },
    [sdk]
  );

  const handleSearch = useCallback(
    (query: string) => {
      setChannels([]);
      void loadChannels(query);
    },
    [loadChannels]
  );

  useEffect(() => {
    void loadChannels("");
  }, [loadChannels]);

  return (
    <Stack direction="column" spacing={4} sx={{ p: 5 }}>
      <Box>
        <Stack direction="row" spacing={4} alignItems="center" mb={2}>
          <IconButton
            size="small"
            variant="outlined"
            iconType="chevron-left"
            onClick={() => {
              history.replace(`${SHIFT_DISCOVERY_PATH}`);
            }}
          />
          <Text semibold variant="h4">
            Chatrooms
          </Text>
        </Stack>
      </Box>
      <SearchBar onSearch={handleSearch} />
      <CreateChannelCard loadChannels={loadChannels} />
      <ChannelList
        channels={channels}
        loading={loading}
        favorites={favorites}
        onToggleFavorite={handleToggleFavorite}
      />
    </Stack>
  );
}

export function ChatroomPageContainer(): JSX.Element {
  return (
    <PageWrapper
      sx={{
        ...getSafeAreaPaddings({
          top: true,
        }),
      }}
    >
      <ChatroomsPage />
      <Navbar />
    </PageWrapper>
  );
}
